import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { errorMessage, request } from '../shared/api/hotel';
import type { Workspace } from '../shared/domain/hotel';
import { WorkspaceContext } from './workspace-context';
import type { Toast } from './workspace-context';
import { usePreferences } from './preferences';
import { Icon } from '../shared/ui/Icon';

export function WorkspaceProvider({ children }: PropsWithChildren) {
    const [data, setData] = useState<Workspace | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [toasts, setToasts] = useState<Toast[]>([]);
    const serial = useRef(0);
    const toastID = useRef(0);
    const preferences = usePreferences();

    const notify = useCallback((message: string, tone: Toast['tone'] = 'success') => {
        setToasts(current => [...current.slice(-2), { id: ++toastID.current, message, tone }]);
    }, []);
    const refresh = useCallback(async () => {
        const sequence = ++serial.current;
        setLoading(true);
        try {
            const result = await request<Workspace>('workspace');
            if (sequence === serial.current) { setData(result); setError(null); }
        } catch (error) {
            if (sequence === serial.current) setError(errorMessage(error));
            throw error;
        } finally { if (sequence === serial.current) setLoading(false); }
    }, []);
    const mutate = useCallback(async <T,>(path: string, body: unknown, message: string, key?: string): Promise<T> => {
        const result = await request<T>(path, body, key);
        try { await refresh(); notify(message); }
        catch { notify(`${message} The refreshed view could not load; use Refresh before making another change.`, 'warning'); }
        return result;
    }, [notify, refresh]);

    const invalidate = useCallback(() => { serial.current += 1; }, []);
    useEffect(() => {
        const timer = window.setTimeout(() => { void refresh().catch(() => undefined); }, 0);
        return () => { window.clearTimeout(timer); invalidate(); };
    }, [invalidate, refresh]);
    useEffect(() => {
        if (toasts.length === 0) return;
        const timer = window.setTimeout(() => setToasts(current => current.slice(1)), 6500);
        return () => window.clearTimeout(timer);
    }, [toasts]);
    useEffect(() => {
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const apply = () => {
            document.documentElement.dataset.theme = preferences.theme === 'system' ? (media.matches ? 'dark' : 'light') : preferences.theme;
            document.documentElement.dataset.density = preferences.density;
        };
        apply();
        media.addEventListener('change', apply);
        return () => media.removeEventListener('change', apply);
    }, [preferences]);
    const value = useMemo(() => ({ data, error, loading, refresh, notify, mutate }), [data, error, loading, refresh, notify, mutate]);
    return <WorkspaceContext.Provider value={value}>
        {children}
        <div className="toast-stack" aria-live="polite" aria-atomic="false">
            {toasts.map(toast => <div className={`toast toast-${toast.tone}`} key={toast.id}>
                <Icon name={toast.tone === 'success' ? 'check-circle' : 'info'} />
                <span>{toast.message}</span>
                <button className="icon-button" aria-label="Dismiss notification" onClick={() => setToasts(current => current.filter(item => item.id !== toast.id))}><Icon name="x" /></button>
            </div>)}
        </div>
    </WorkspaceContext.Provider>;
}
